import React, { useRef, Fragment, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Webcam from "react-webcam";
import { twMerge } from "tailwind-merge";

import DamageService from "../../services/DamageService";
import { useStoreBase } from "../../store";
import { Types } from "../../base-components/Notification";
import { ReactComponent as CameraIcon } from "../../assets/icons/camera-1.svg";
import { ReactComponent as Refresh } from "../../assets/icons/refresh-ccw.svg";
import Button from "../../base-components/Button";
import Typography from "../../base-components/Typography";

import useStore2 from "../../store/hook";

const SIDE_LABEL: Record<string, string> = {
  front: "Урд тал",
  back: "Хойд тал",
  left: "Зүүн тал",
  right: "Баруун тал",
};

const CameraMask = () => {
  const params = useParams();
  const navigate = useNavigate();

  const setImageChanged = useStore2.useSetImageChanged();
  const name = useStore2.useName();
  const page = useStore2.usePage();
  const [image, setImage] = useState("");
  const [flash, setFlash] = useState(false);
  const [saving, setSaving] = useState(false);

  const webcamRef = useRef<Webcam>(null);
  const videoConstraints: MediaTrackConstraints = {
    facingMode: "environment",
  };

  const sideLabel =
    typeof params?.location === "string" ? SIDE_LABEL[params.location] : "";

  const capturePhoto = React.useCallback(async () => {
    const imageSrc = webcamRef?.current?.getScreenshot();
    if (typeof imageSrc === "string") {
      setFlash(true);
      setTimeout(() => setFlash(false), 180);
      setImage(imageSrc);
      setImageChanged(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamRef]);

  function handleContinue() {
    const payload: {
      [_: string]: string;
    } = {};
    if (typeof params?.location === "string" && params?.location !== "") {
      payload[params?.location] = image;
      setSaving(true);
      DamageService.save(name, payload)
        .then((response) => {
          if (response === "success") {
            navigate(page);
            return;
          }
          useStoreBase.setState({
            notification: {
              message: "Зураг хадгалагдсангүй. Дахин оролдоно уу.",
              type: Types.warning,
            },
          });
        })
        .finally(() => setSaving(false));
    }
  }

  return (
    <Fragment>
      {image ? (
        <div className="absolute inset-0 p-4 py-6 bg-gray flex flex-col">
          {sideLabel && (
            <Typography variant="Title" size="md" className="mb-3">
              {sideLabel}
            </Typography>
          )}
          <div className="flex-1 flex items-center justify-center rounded-card bg-white shadow-card overflow-hidden">
            <img src={image} alt="damage" className="max-h-full max-w-full object-contain" />
          </div>
          <Typography size="sm" className="text-center text-black-64 mt-3">
            Зураг бүдэг бол дахин дарж авна уу.
          </Typography>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Button
              variant="primary-outlined"
              icon={<Refresh className="w-[18px] h-[18px]" />}
              onClick={() => setImage("")}
            >
              Дахин дарах
            </Button>
            <Button disabled={saving} onClick={() => handleContinue()}>
              {saving ? "Хадгалж байна..." : "Үргэлжлүүлэх"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="absolute inset-0 bg-black">
          <div className="h-full w-full inset-0 flex justify-center overflow-hidden">
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              className="h-full w-full object-cover"
            />
          </div>

          {/* Capture flash feedback */}
          <div
            className={twMerge([
              "pointer-events-none absolute inset-0 bg-white transition-opacity duration-150",
              flash ? "opacity-80" : "opacity-0",
            ])}
          />

          {/* Framing guide */}
          <div className="pointer-events-none absolute inset-6 top-20 bottom-40">
            {["top-0 left-0 border-t-2 border-l-2 rounded-tl-xl", "top-0 right-0 border-t-2 border-r-2 rounded-tr-xl", "bottom-0 left-0 border-b-2 border-l-2 rounded-bl-xl", "bottom-0 right-0 border-b-2 border-r-2 rounded-br-xl"].map((cls) => (
              <div
                key={cls}
                className={twMerge(["absolute h-10 w-10 border-white/80", cls])}
              />
            ))}
          </div>

          {sideLabel && (
            <div className="absolute top-6 inset-x-0 flex justify-center">
              <span className="rounded-full bg-black/45 backdrop-blur-sm px-4 py-1.5">
                <Typography variant="Label" size="lg" className="text-white">
                  {sideLabel}
                </Typography>
              </span>
            </div>
          )}

          <div className="absolute inset-x-0 bottom-8">
            <div className="grid gap-5 px-9">
              <Typography size="md" className="text-center text-white">
                Машиныг рамк дотор бүтэн багтаана уу.
              </Typography>
              <div className="flex justify-center">
                <button
                  type="button"
                  aria-label="Зураг авах"
                  onClick={capturePhoto}
                  className={twMerge([
                    "h-[72px] w-[72px] rounded-full bg-white/25 border-2 border-white flex items-center justify-center",
                    "transition-transform duration-150 ease-out outline-none",
                    "[&:active]:scale-90 focus-visible:shadow-focus",
                  ])}
                >
                  <span className="h-14 w-14 rounded-full bg-white flex items-center justify-center">
                    <CameraIcon className="w-6 h-6" />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default CameraMask;
